// src/pages/Kyc.tsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Camera, Upload, CheckCircle, Clock, AlertCircle, ChevronRight, ChevronLeft } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const countries = [
  { code: "+1", name: "USA/CANADA" },
  { code: "+44", name: "UK" },
  { code: "+91", name: "INDIA" },
  { code: "+92", name: "PAKISTAN" },
  { code: "+86", name: "CHINA" },
  { code: "+81", name: "JAPAN" },
  { code: "+49", name: "GERMANY" },
  { code: "+33", name: "FRANCE" },
  { code: "+61", name: "AUSTRALIA" },
  { code: "+971", name: "UAE" },
  { code: "+966", name: "SAUDI ARABIA" },
  { code: "+20", name: "EGYPT" },
  { code: "+234", name: "NIGERIA" },
  { code: "+55", name: "BRAZIL" },
  { code: "+52", name: "MEXICO" },
  { code: "+7", name: "RUSSIA" },
  { code: "+82", name: "SOUTH KOREA" },
  { code: "+39", name: "ITALY" },
  { code: "+34", name: "SPAIN" },
  { code: "+31", name: "NETHERLANDS" },
];

export default function Kyc() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<"none" | "pending" | "approved" | "rejected">("none");
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+92");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [idFront, setIdFront] = useState<File | null>(null);
  const [idBack, setIdBack] = useState<File | null>(null);
  const [liveImage, setLiveImage] = useState<File | null>(null);

  const [idFrontPreview, setIdFrontPreview] = useState("");
  const [idBackPreview, setIdBackPreview] = useState("");
  const [liveImagePreview, setLiveImagePreview] = useState("");

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraActive, setCameraActive] = useState(false);

  // Helper: compress image to reduce size (max width 1024px, JPEG quality 0.7)
  const compressImage = (file: File, maxWidth = 1024, quality = 0.7): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx?.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, ".jpg"), {
                  type: "image/jpeg",
                });
                resolve(compressedFile);
              } else {
                reject(new Error("Compression failed"));
              }
            },
            "image/jpeg",
            quality
          );
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const authRes = await fetch("/api/auth", { method: "POST", credentials: "include" });
        if (!authRes.ok) return navigate("/login");
        const authData = await authRes.json();
        if (!authData.success || !authData.user) return navigate("/login");

        const statusRes = await fetch("/api/kyc/status", { credentials: "include" });
        const statusData = await statusRes.json();
        const kyStatus = statusData.status || statusData.kycStatus;
        setStatus(kyStatus as "none" | "pending" | "approved" | "rejected");
      } catch (err) {
        console.error(err);
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();

    const savedStep = localStorage.getItem("kyc_step");
    if (savedStep) setStep(parseInt(savedStep));
    const savedEmail = localStorage.getItem("kyc_email");
    if (savedEmail) setEmail(savedEmail);
    const savedCountryCode = localStorage.getItem("kyc_countryCode");
    if (savedCountryCode) setCountryCode(savedCountryCode);
    const savedPhone = localStorage.getItem("kyc_phone");
    if (savedPhone) setPhoneNumber(savedPhone);
  }, [navigate]);

  useEffect(() => {
    localStorage.setItem("kyc_step", step.toString());
    localStorage.setItem("kyc_email", email);
    localStorage.setItem("kyc_countryCode", countryCode);
    localStorage.setItem("kyc_phone", phoneNumber);
  }, [step, email, countryCode, phoneNumber]);

  useEffect(() => {
    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Compressed file handler for ID front/back
  const handleFileChange = async (field: "idFront" | "idBack", file: File | null) => {
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      if (field === "idFront") {
        setIdFront(compressed);
        const reader = new FileReader();
        reader.onload = (e) => setIdFrontPreview(e.target?.result as string);
        reader.readAsDataURL(compressed);
      } else {
        setIdBack(compressed);
        const reader = new FileReader();
        reader.onload = (e) => setIdBackPreview(e.target?.result as string);
        reader.readAsDataURL(compressed);
      }
    } catch (err) {
      toast({ title: "ERROR", description: "Failed to compress image", variant: "destructive" });
    }
  };

  // Compressed file handler for selfie upload
  const handleSelfieUpload = async (file: File | null) => {
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      setLiveImage(compressed);
      const reader = new FileReader();
      reader.onload = (e) => setLiveImagePreview(e.target?.result as string);
      reader.readAsDataURL(compressed);
    } catch (err) {
      toast({ title: "ERROR", description: "Failed to compress image", variant: "destructive" });
    }
  };

  // Camera functions kept but not used (they show a message)
  const startCamera = async () => {
    toast({ title: "CAMERA DISABLED", description: "Please use the upload button instead.", variant: "default" });
  };
  const stopCamera = () => {};
  const captureImage = () => {};

  const nextStep = () => {
    if (step === 0 && !email) {
      toast({ title: "EMAIL REQUIRED", variant: "destructive" });
      return;
    }
    if (step === 1 && (!countryCode || !phoneNumber)) {
      toast({ title: "PHONE NUMBER REQUIRED", variant: "destructive" });
      return;
    }
    if (step === 2 && !idFront) {
      toast({ title: "ID FRONT REQUIRED", variant: "destructive" });
      return;
    }
    if (step === 3 && !idBack) {
      toast({ title: "ID BACK REQUIRED", variant: "destructive" });
      return;
    }
    if (step === 4 && !liveImage) {
      toast({ title: "SELFIE REQUIRED", variant: "destructive" });
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  const handleSubmit = async () => {
    if (!email || !phoneNumber || !idFront || !idBack || !liveImage) {
      toast({ title: "MISSING DATA", description: "PLEASE COMPLETE ALL STEPS", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const payload = new FormData();
    payload.append("email", email);
    payload.append("phone", `${countryCode}${phoneNumber}`);
    payload.append("idFront", idFront);
    payload.append("idBack", idBack);
    payload.append("liveImage", liveImage);

    try {
      const res = await fetch("/api/kyc/submit", { method: "POST", body: payload, credentials: "include" });
      const data = await res.json();
      if (res.ok) {
        toast({ title: "SUBMITTED", description: "YOUR KYC REQUEST IS PENDING APPROVAL" });
        setStatus("pending");
        localStorage.removeItem("kyc_step");
        localStorage.removeItem("kyc_email");
        localStorage.removeItem("kyc_countryCode");
        localStorage.removeItem("kyc_phone");
      } else {
        toast({ title: "ERROR", description: data.error || "SUBMISSION FAILED", variant: "destructive" });
      }
    } catch (err) {
      toast({ title: "ERROR", description: "NETWORK ERROR", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center uppercase">LOADING...</div>
      </div>
    );
  }

  if (status === "approved") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card rounded-lg p-8 max-w-md text-center shadow-lg">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2 uppercase">KYC COMPLETED</h1>
          <p className="text-muted-foreground uppercase">YOUR IDENTITY VERIFICATION HAS BEEN APPROVED.</p>
        </div>
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card rounded-lg p-8 max-w-md text-center shadow-lg">
          <Clock className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2 uppercase">KYC PENDING</h1>
          <p className="text-muted-foreground uppercase">YOUR KYC REQUEST IS UNDER REVIEW. PLEASE WAIT FOR ADMIN APPROVAL.</p>
        </div>
      </div>
    );
  }

  if (status === "rejected") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card rounded-lg p-8 max-w-md text-center shadow-lg">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2 uppercase">KYC REJECTED</h1>
          <p className="text-muted-foreground uppercase mb-4">YOUR KYC SUBMISSION WAS REJECTED. PLEASE CONTACT SUPPORT.</p>
          <Button onClick={() => setStatus("none")} className="bg-yellow-400 hover:bg-yellow-500 text-black uppercase">
            SUBMIT AGAIN
          </Button>
        </div>
      </div>
    );
  }

  // Step 0: Email
  if (step === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card rounded-lg p-6 shadow-lg">
          <h1 className="text-2xl font-bold mb-6 uppercase">KYC VERIFICATION</h1>
          <div className="space-y-4">
            <div>
              <Label className="uppercase">EMAIL ADDRESS</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="YOUR@EMAIL.COM"
                required
              />
            </div>
            <Button onClick={nextStep} className="w-full bg-yellow-400 hover:bg-yellow-500 text-black uppercase">
              CONTINUE <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Step 1: Phone with country code
  if (step === 1) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card rounded-lg p-6 shadow-lg">
          <h1 className="text-2xl font-bold mb-6 uppercase">PHONE NUMBER</h1>
          <div className="space-y-4">
            <div>
              <Label className="uppercase">COUNTRY CODE</Label>
              <select
                className="w-full p-2 border rounded bg-background uppercase"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
              >
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name} ({c.code})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="uppercase">PHONE NUMBER</Label>
              <Input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="1234567890"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={prevStep} className="flex-1 uppercase">BACK</Button>
              <Button onClick={nextStep} className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black uppercase">
                CONTINUE <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: ID Front
  if (step === 2) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card rounded-lg p-6 shadow-lg">
          <h1 className="text-2xl font-bold mb-6 uppercase">ID CARD (FRONT)</h1>
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-4">
              {idFrontPreview ? (
                <img src={idFrontPreview} alt="ID Front" className="max-h-48 rounded border" />
              ) : (
                <div className="w-full h-32 border-2 border-dashed rounded flex items-center justify-center text-muted-foreground uppercase">
                  NO FILE SELECTED
                </div>
              )}
              <Button variant="outline" onClick={() => document.getElementById("idFrontInput")?.click()} className="uppercase">
                <Upload className="w-4 h-4 mr-2" /> UPLOAD FRONT
              </Button>
              <input
                id="idFrontInput"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileChange("idFront", e.target.files?.[0] || null)}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={prevStep} className="flex-1 uppercase">BACK</Button>
              <Button onClick={nextStep} className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black uppercase">
                CONTINUE <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: ID Back
  if (step === 3) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card rounded-lg p-6 shadow-lg">
          <h1 className="text-2xl font-bold mb-6 uppercase">ID CARD (BACK)</h1>
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-4">
              {idBackPreview ? (
                <img src={idBackPreview} alt="ID Back" className="max-h-48 rounded border" />
              ) : (
                <div className="w-full h-32 border-2 border-dashed rounded flex items-center justify-center text-muted-foreground uppercase">
                  NO FILE SELECTED
                </div>
              )}
              <Button variant="outline" onClick={() => document.getElementById("idBackInput")?.click()} className="uppercase">
                <Upload className="w-4 h-4 mr-2" /> UPLOAD BACK
              </Button>
              <input
                id="idBackInput"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileChange("idBack", e.target.files?.[0] || null)}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={prevStep} className="flex-1 uppercase">BACK</Button>
              <Button onClick={nextStep} className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black uppercase">
                CONTINUE <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 4: Live Selfie – Upload (no camera)
  if (step === 4) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card rounded-lg p-6 shadow-lg">
          <h1 className="text-2xl font-bold mb-6 uppercase">LIVE SELFIE</h1>
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-4">
              {liveImagePreview ? (
                <img src={liveImagePreview} alt="Selfie" className="max-h-48 rounded border" />
              ) : (
                <div className="w-full h-32 border-2 border-dashed rounded flex items-center justify-center text-muted-foreground uppercase">
                  NO SELFIE UPLOADED
                </div>
              )}
              <Button
                variant="outline"
                onClick={() => document.getElementById("selfieInput")?.click()}
                className="uppercase bg-yellow-400 hover:bg-yellow-500 text-black"
              >
                <Upload className="w-4 h-4 mr-2" /> UPLOAD SELFIE
              </Button>
              <input
                id="selfieInput"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleSelfieUpload(e.target.files?.[0] || null)}
              />
              {liveImagePreview && (
                <Button
                  variant="outline"
                  className="w-full uppercase"
                  onClick={() => {
                    setLiveImage(null);
                    setLiveImagePreview("");
                  }}
                >
                  RETAKE / RE-UPLOAD
                </Button>
              )}
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={prevStep} className="flex-1 uppercase">BACK</Button>
              <Button onClick={nextStep} className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black uppercase" disabled={!liveImagePreview}>
                CONTINUE <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 5: Review and submit
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-card rounded-lg p-6 shadow-lg">
        <h1 className="text-2xl font-bold mb-6 uppercase">REVIEW & SUBMIT</h1>
        <div className="space-y-3 text-sm uppercase">
          <div><strong>EMAIL:</strong> {email}</div>
          <div><strong>PHONE:</strong> {countryCode}{phoneNumber}</div>
          <div><strong>ID FRONT:</strong> UPLOADED</div>
          <div><strong>ID BACK:</strong> UPLOADED</div>
          <div><strong>LIVE SELFIE:</strong> UPLOADED</div>
        </div>
        <div className="flex gap-2 mt-6">
          <Button variant="outline" onClick={prevStep} className="flex-1 uppercase">BACK</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black uppercase">
            {submitting ? "SUBMITTING..." : "SUBMIT KYC"}
          </Button>
        </div>
      </div>
    </div>
  );
}
